import {apiSlice} from './apiSlice';

/**
 * Quản lý thành viên (admin) — features/Member. Mọi response bọc {status,code,message,data};
 * transformResponse lấy body?.data. Tag 'Member' để tự refetch sau mỗi thao tác admin.
 */
export const memberApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getMembers: builder.query({
			query: ({keyword, plan, status, page, page_size} = {}) => {
				const params = {};
				if (keyword) params.keyword = keyword;
				if (plan) params.plan = plan;
				if (status) params.status = status;
				if (page) params.page = page;
				if (page_size) params.page_size = page_size;
				return {url: 'members', method: 'get', params};
			},
			// BE trả {items,total,page,page_size} (phân trang server-side).
			transformResponse: (body) => body?.data || {items: [], total: 0, page: 1, page_size: 20},
			providesTags: ['Member'],
		}),
		getMember: builder.query({
			query: (id) => ({url: `members/${id}`, method: 'get'}),
			transformResponse: (body) => body?.data || null,
			providesTags: (result, error, id) => [{type: 'Member', id}],
		}),
		setMemberPlan: builder.mutation({
			query: ({id, ...body}) => ({url: `members/${id}/plan`, method: 'post', data: body}),
			invalidatesTags: (result, error, {id}) => [{type: 'Member', id}, 'Member'],
		}),
		setMemberTokens: builder.mutation({
			query: ({id, ...body}) => ({url: `members/${id}/tokens`, method: 'post', data: body}),
			invalidatesTags: (result, error, {id}) => [{type: 'Member', id}, 'Member'],
		}),
		getTokenLedger: builder.query({
			query: (id) => ({url: `members/${id}/tokens/ledger`, method: 'get'}),
			transformResponse: (body) => body?.data || [],
			providesTags: (result, error, id) => [{type: 'Member', id}],
		}),
		setMemberStatus: builder.mutation({
			query: ({id, status}) => ({url: `members/${id}/status`, method: 'post', data: {status}}),
			invalidatesTags: (result, error, {id}) => [{type: 'Member', id}, 'Member'],
		}),
		memberLogoutAll: builder.mutation({
			query: (id) => ({url: `members/${id}/logout-all`, method: 'post'}),
			invalidatesTags: (result, error, id) => [{type: 'Member', id}],
		}),
		resetMemberPassword: builder.mutation({
			query: ({id, password}) => ({url: `members/${id}/reset-password`, method: 'post', data: {password}}),
			invalidatesTags: (result, error, {id}) => [{type: 'Member', id}],
		}),
		getMemberActivity: builder.query({
			query: (id) => ({url: `members/${id}/activity`, method: 'get'}),
			transformResponse: (body) => body?.data || [],
			providesTags: (result, error, id) => [{type: 'Member', id}],
		}),
		// Yêu cầu nâng cấp gói (user gửi ở /subscription, admin duyệt/từ chối ở đây).
		getPlanRequests: builder.query({
			query: (status) => ({url: 'members/plan-requests', method: 'get', params: status ? {status} : {}}),
			transformResponse: (body) => body?.data || {items: [], pending_count: 0},
			providesTags: ['PlanRequest'],
		}),
		resolvePlanRequest: builder.mutation({
			query: ({id, ...body}) => ({url: `members/plan-requests/${id}`, method: 'post', data: body}),
			// approve đổi gói của user → làm mới cả danh sách thành viên.
			invalidatesTags: ['PlanRequest', 'Member'],
		}),
		// Đơn mua Gói Extra (user gửi ở /subscription, admin duyệt/từ chối ở đây).
		getExtraPurchases: builder.query({
			query: (status) => ({url: 'members/extra-purchases', method: 'get', params: status ? {status} : {}}),
			transformResponse: (body) => body?.data || {items: [], pending_count: 0},
			providesTags: ['ExtraPurchase'],
		}),
		resolveExtraPurchase: builder.mutation({
			query: ({id, ...body}) => ({url: `members/extra-purchases/${id}`, method: 'post', data: body}),
			invalidatesTags: ['ExtraPurchase', 'Member'],
		}),
		// Tặng trực tiếp bài Extra cho 1 thành viên (tạo luôn đơn approved pack_id=0).
		grantMemberExtra: builder.mutation({
			query: ({id, ...body}) => ({url: `members/${id}/extra`, method: 'post', data: body}),
			invalidatesTags: (result, error, {id}) => [{type: 'Member', id}, 'ExtraPurchase'],
		}),
		// Khoá AI riêng (BYO) — chỉ admin cập nhật cho thành viên.
		getMemberAiKey: builder.query({
			query: (id) => ({url: `members/${id}/api-key`, method: 'get'}),
			transformResponse: (body) => body?.data || {configured: false, providers: []},
			providesTags: (result, error, id) => [{type: 'Member', id}],
		}),
		setMemberAiKey: builder.mutation({
			query: ({id, ...body}) => ({url: `members/${id}/api-key`, method: 'put', data: body}),
			invalidatesTags: (result, error, {id}) => [{type: 'Member', id}],
		}),
		deleteMemberAiKey: builder.mutation({
			query: (id) => ({url: `members/${id}/api-key`, method: 'delete'}),
			invalidatesTags: (result, error, id) => [{type: 'Member', id}],
		}),
	}),
});

export const {
	useGetMembersQuery,
	useGetMemberQuery,
	useSetMemberPlanMutation,
	useSetMemberTokensMutation,
	useGetTokenLedgerQuery,
	useSetMemberStatusMutation,
	useMemberLogoutAllMutation,
	useResetMemberPasswordMutation,
	useGetMemberActivityQuery,
	useGetMemberAiKeyQuery,
	useSetMemberAiKeyMutation,
	useDeleteMemberAiKeyMutation,
	useGetPlanRequestsQuery,
	useResolvePlanRequestMutation,
	useGetExtraPurchasesQuery,
	useResolveExtraPurchaseMutation,
	useGrantMemberExtraMutation,
} = memberApiSlice;
